/* eslint-disable react-refresh/only-export-components -- bewusst co-lozierter Provider+Hook bzw. Helfer-Export; react-refresh ist eine reine Dev-HMR-Optimierung, kein Prod-/Qualitaetsthema. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser, LoginRequest, LoginResponse } from '@brandschutz/shared';
import { api, isApiError, onUnauthorizedListener, setAccessToken } from './api';
import { leereMedienTokenCache } from '@/features/medien/api';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthState | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: ProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const { data } = await api.get<AuthUser>('/auth/me');
      setUser(data);
      return data;
    } catch (err) {
      setUser(null);
      // Silently fail — bedeutet schlicht: nicht eingeloggt
      if (import.meta.env.DEV && !isApiError(err)) {
        console.warn('[auth] /me failed', err);
      }
      return null;
    }
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<AuthUser> => {
    const { data } = await api.post<LoginResponse>('/auth/login', credentials);
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    } finally {
      setAccessToken(null);
      setUser(null);
      // Datei-Token für Medien leben zehn Minuten und hängen am Medium, nicht
      // an der Person — auf einem geteilten Gerät dürfen sie die Abmeldung
      // nicht überleben.
      leereMedienTokenCache();
    }
  }, []);

  // Auf Mount: versuche silent-refresh
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.post<{ accessToken: string }>('/auth/refresh');
        if (!mounted) return;
        setAccessToken(data.accessToken);
        await fetchMe();
      } catch {
        if (mounted) setUser(null);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [fetchMe]);

  // Bei 401 nach Refresh-Versuch: User zurücksetzen
  useEffect(() => {
    onUnauthorizedListener(() => {
      setUser(null);
      leereMedienTokenCache();
    });
    return () => onUnauthorizedListener(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      logout,
      refreshUser: fetchMe,
    }),
    [user, isLoading, login, logout, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
